import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router'
import type { ActionModuleResponse } from '@/api/model'
import { PermissionEntityType } from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { Tabs, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import { usePermissions } from '@/hooks/usePermissions'
import { useGetApiCompaniesCurrentActionmodulesActionModuleId } from '@/api/generated/company-management-user/company-management-user'
import { isManualBulkUploadModule } from './lib'

/** Context handed from the detail page down to each tab via <Outlet>. */
export interface ActionModuleDetailContext {
  module: ActionModuleResponse
}

/** Read the module in a tab component: `const { module } = useActionModuleDetail()`. */
export function useActionModuleDetail() {
  return useOutletContext<ActionModuleDetailContext>()
}

interface TabDef {
  /** URL segment under /action-modules/:moduleId/. */
  value: string
  label: string
  visible: boolean
}

/**
 * Composition root of the per-module detail page (Aktionsmodule / <name>). Loads the
 * module, renders the header + tab bar and, per-module, gates which tabs are shown: the
 * tabs need Incentivate, and Import/Export additionally needs a Manual source that allows
 * bulk upload. Tab content renders through the <Outlet> (nested routes), each guarded
 * coarsely at route level; this page redirects away from a tab the user may not see on
 * *this* module.
 */
export default function ActionModuleDetailPage() {
  const { t } = useTranslation('action-modules-detail')
  const { moduleId } = useParams()
  const id = Number(moduleId)
  const navigate = useNavigate()
  const location = useLocation()
  const { userHasEntityPermission } = usePermissions()

  const { data, isLoading, isError } = useGetApiCompaniesCurrentActionmodulesActionModuleId(id)
  const module: ActionModuleResponse | null = useMemo(() => {
    const raw = (data as any)?.data ?? data
    return raw && typeof raw === 'object' && 'id' in raw ? (raw as ActionModuleResponse) : null
  }, [data])

  // Ensure the module-name breadcrumb crumb is present even on deep-links / the base→incentivation
  // redirect (where no navigation state was passed). Runs once per module name.
  useEffect(() => {
    const label = module?.name
    if (!label) return
    const existing = (location.state as any)?.breadcrumbs
    const hasCrumb = Array.isArray(existing) && existing.some((crumb: any) => crumb?.label === label)
    if (!hasCrumb) {
      navigate(location.pathname, {
        replace: true,
        state: { ...(location.state as object), breadcrumbs: [{ label }] },
      })
    }
  }, [module?.name, location.pathname, location.state, navigate])

  const canIncentivate = userHasEntityPermission(PermissionEntityType.ActionModules, 'Incentivate', id)
  const canBulkUpload = module ? isManualBulkUploadModule(module) : false

  const tabs: TabDef[] = [
    { value: 'incentivation', label: t('tabs.incentivation'), visible: canIncentivate },
    { value: 'import-export', label: t('tabs.importExport'), visible: canIncentivate && canBulkUpload },
    { value: 'module-settings', label: t('tabs.moduleSettings'), visible: canIncentivate },
  ]
  const visibleTabs = tabs.filter((tab) => tab.visible)

  // Last path segment = current tab (base redirects to incentivation via the index route).
  const currentTab = location.pathname.split('/').filter(Boolean).pop() ?? ''

  const headerTitle = `${t('header.prefix')} / ${module?.name ?? ''}`.trimEnd()

  if (isLoading) {
    return (
      <PageLayout>
        <PageHeader title={t('header.prefix')} />
        <LoaderGeneral isLoading />
      </PageLayout>
    )
  }

  if (isError || !module) {
    return (
      <PageLayout>
        <PageHeader title={t('header.prefix')} />
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          {t('notFound')}
        </div>
      </PageLayout>
    )
  }

  // The current tab is not visible for this user on this module → send them to the first
  // one they may see (or the landing page if none).
  const onVisibleTab = visibleTabs.some((tab) => tab.value === currentTab)
  if (!onVisibleTab) {
    return <Navigate to={visibleTabs[0]?.value ?? '/'} replace />
  }

  return (
    <PageLayout>
      <PageHeader title={headerTitle} />

      <Tabs
        value={currentTab}
        onValueChange={(value) => navigate(value, { state: location.state })}
        className="min-w-0"
      >
        <TabsList className="flex-wrap h-auto! gap-1 [&_[data-slot=tabs-trigger]]:h-auto">
          {visibleTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet context={{ module } satisfies ActionModuleDetailContext} />
      </div>
    </PageLayout>
  )
}
